import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useOutsideClick } from 'hooks';
import styled, { css } from 'styled-components';
import { LANGS } from 'consts';
import { isMobileSelector } from 'redux/selectors/global';
import Icon, { IconName } from 'components/core/Icon/Icon';
import Button, { ButtonVariants } from 'components/core/Button/Button';
import LanguageMenu from 'components/containers/LanguagePicker/Options';
import { ThemeBreakpoints, ThemeFamilies } from 'models';
import { trackLangPicker } from 'utilities/analytics/global';

interface IProps {
  className?: string;
  excludedLangs?: string[];
}

const LanguagePicker = ({ className, excludedLangs }: IProps) => {
  const {
    i18n: { language }
  } = useTranslation();
  const isMobile = useSelector(isMobileSelector);

  const [opened, setOpened] = useState(false);

  const pickerRef = useRef() as React.MutableRefObject<HTMLDivElement>;
  useOutsideClick(pickerRef, () => closeMenu(), 'drawer-mask');

  const [country] = language?.split('-') || [];

  const handlePickerClick = (): void => {
    if (opened) {
      setOpened(false);
      return;
    }
    trackLangPicker();

    setOpened(true);
  };

  const closeMenu = () => setOpened(false);

  return (
    <Wrapper ref={pickerRef} data-testid="language">
      <StyledButton
        className={className}
        name="language"
        staticSize={ThemeBreakpoints.MEDIUM}
        themeFamily={ThemeFamilies.GREY}
        variant={ButtonVariants.TEXT}
        onClick={handlePickerClick}
        fixedHeight={false}
      >
        <StyledIcon name={IconName.WORLD} />
        <Text>
          {isMobile
            ? `${country?.toUpperCase()}`
            : `${LANGS[language as keyof typeof LANGS]?.lang}`}
        </Text>
      </StyledButton>
      <LanguageMenu
        selectedLanguage={language}
        excludedLangs={excludedLangs}
        isMenuOpen={opened}
        closeMenu={closeMenu}
      />
    </Wrapper>
  );
};

export default LanguagePicker;

const Wrapper = styled.div`
  position: relative;

  [data-testid='option-list'] {
    overflow-x: hidden;
  }
`;

const StyledIcon = styled(Icon)`
  ${({ theme }) => {
    const { isMobile } = theme.view;

    return (
      isMobile &&
      css`
        font-size: 1.8rem;
        line-height: unset;
      `
    );
  }}
`;

const StyledButton = styled(Button)`
  ${({ theme }) => {
    const { isMobile } = theme.view;

    return (
      isMobile &&
      css`
        display: flex;
        align-items: baseline;
      `
    );
  }}
`;

const Text = styled.span`
  margin-left: ${({ theme }) => (theme.view.isMobile ? '0.3rem' : '0.6rem')};

  ${({ theme }) => {
    const { isMobile } = theme.view;

    return (
      isMobile &&
      css`
        font-size: 1.5rem;
      `
    );
  }}
`;
